import React from 'react';
import HwzComponent from '../../../common/component/HwzComponent';
import Share from '../../../common/model/share/Share';
import {
  Button,
  Modal,
  Tabs,
  Select,
  Tag,
  Avatar,
  Radio,
  InputNumber,
  Checkbox,
} from 'antd';
import {
  CopyOutlined,
  ExclamationCircleFilled,
  UserOutlined,
  TeamOutlined,
  EyeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import DateUtil from '../../../common/util/DateUtil';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import './ShareDialogModal.less';
import ClipboardUtil from '../../../common/util/ClipboardUtil';
import Lang from '../../../common/model/global/Lang';
import User from '../../../common/model/user/User';
import Pager from '../../../common/model/base/Pager';
import Space from '../../../common/model/space/Space';
import { UserRole } from '../../../common/model/user/UserRole';
import {
  ShareExpireOption,
  ShareExpireOptionList,
} from '../../../common/model/share/ShareExpireOption';
import Moon from '../../../common/model/global/Moon';

const { TabPane } = Tabs;
const { Option } = Select;

interface IProps {
  matterUuids: string;
  onSuccess: () => any;
  onClose: () => any;
}

interface IState {
  activeTab: string;
  selectedUsers: User[];
  selectedSpaces: Space[];
  userPager: Pager<User>;
  spacePager: Pager<Space>;
  publicShare: Share | null;

  expireValue: number;
  expireUnit: ShareExpireOption;
  isInfinity: boolean;
  permission: 'PREVIEW_ONLY' | 'FULL';
}

export default class ShareDialogModal extends HwzComponent<IProps, IState> {
  user: User = Moon.getSingleton().user;

  constructor(props: IProps) {
    super(props);

    const defaultActiveTab = this.user.disablePublicShare ? 'private' : 'public';

    this.state = {
      activeTab: defaultActiveTab,
      selectedUsers: [],
      selectedSpaces: [],
      userPager: new Pager<User>(this, User, 10),
      spacePager: new Pager<Space>(this, Space, 10),
      publicShare: null,
      expireValue: 1,
      expireUnit: ShareExpireOption.DAY,
      isInfinity: false,
      permission: 'PREVIEW_ONLY',
    };
  }

  componentDidMount() {
    this.refreshUserList();
    this.refreshSpaceList();
  }

  refreshUserList() {
    const { userPager } = this.state;
    userPager.httpList();
  }

  refreshSpaceList() {
    const { spacePager } = this.state;
    spacePager.setFilterValue('type', 'SHARED');
    spacePager.httpList();
  }

  clipboard(text: string) {
    ClipboardUtil.copy(text, () => {
      MessageBoxUtil.success(Lang.t('copySuccess'));
    });
  }

  private getExpireParams(): { expireInfinity: boolean; expireTime?: Date } {
    const { isInfinity, expireValue, expireUnit } = this.state;
    if (isInfinity) {
      return { expireInfinity: true };
    }

    const option = ShareExpireOptionList.find(opt => opt.value === expireUnit);
    if (!option) {
      return {
        expireInfinity: false,
        expireTime: new Date(Date.now() + 24 * 60 * 60 * 1000),
      };
    }

    const totalMs = expireValue * option.deltaMillisecond;
    return {
      expireInfinity: false,
      expireTime: new Date(Date.now() + totalMs),
    };
  }

  createPublicShare = () => {
    const { matterUuids } = this.props;
    const { permission } = this.state;
    const { expireInfinity, expireTime } = this.getExpireParams();

    const share = new Share();
    share.expireInfinity = expireInfinity;
    if (expireTime) {
      share.expireTime = expireTime;
    }
    share.sharePermission = permission;

    share.httpCreate(
      matterUuids,
      () => {
        this.setState({ publicShare: share }, () => {
          this.copyPublicShare();
        });
      },
      (error: any) => {
        console.error('创建公共分享失败:', error);
        MessageBoxUtil.error(error.message || Lang.t('share.createFailed'));
      }
    );
  };

  copyPublicShare = () => {
    const { publicShare } = this.state;

    if (!publicShare) {
      MessageBoxUtil.warning(Lang.t('share.publicShareNotCreated'));
      return;
    }

    const url = `${publicShare.getLink()}?code=${publicShare.code}`;
    this.clipboard(url);
    MessageBoxUtil.success(Lang.t('share.copyPublicSuccess'));
  };

  handleTabChange = (activeKey: string) => {
    this.setState({ activeTab: activeKey });
  };

  handleUserSelect = (selectedItems: any[]) => {
    const { userPager } = this.state;
    const selectedUsers = selectedItems.map(item => {
      if (item instanceof User) {
        return item;
      }
      return userPager.data.find(user => user.uuid === item) || new User();
    });

    this.setState({ selectedUsers });
  };

  handleSpaceSelect = (selectedItems: any[]) => {
    const { spacePager } = this.state;
    const selectedSpaces = selectedItems.map(item => {
      if (item instanceof Space) {
        return item;
      }
      return spacePager.data.find(space => space.uuid === item) || new Space();
    });

    this.setState({ selectedSpaces });
  };

  createPrivateShare = () => {
    const { selectedUsers, selectedSpaces } = this.state;
    const { matterUuids, onSuccess } = this.props;
    const { permission } = this.state;
    const { expireInfinity, expireTime } = this.getExpireParams();

    if (selectedUsers.length === 0 && selectedSpaces.length === 0) {
      MessageBoxUtil.warning(Lang.t('share.selectUserOrSpace'));
      return;
    }

    const targetUserUuids = selectedUsers
      .map(user => user.uuid)
      .filter((uuid): uuid is string => uuid !== null);

    const targetSpaceUuids = selectedSpaces
      .map(space => space.uuid)
      .filter((uuid): uuid is string => uuid !== null);

    if (!matterUuids) {
      MessageBoxUtil.error(Lang.t('share.noValidFiles'));
      return;
    }

    const share = new Share();
    share.expireInfinity = expireInfinity;
    if (expireTime) {
      share.expireTime = expireTime;
    }
    share.sharePermission = permission;

    share.httpCreatePrivateShare(
      targetUserUuids,
      targetSpaceUuids,
      matterUuids,
      () => {
        MessageBoxUtil.success(Lang.t('share.createSuccess'));
        onSuccess();
      },
      (error: any) => {
        console.error('创建私有分享失败:', error);
        MessageBoxUtil.error(error.message || Lang.t('share.createFailed'));
      }
    );
  };

  getUserRoleColor(role: UserRole): string {
    const roleColorMap: { [key in UserRole]: string } = {
      [UserRole.ADMINISTRATOR]: 'red',
      [UserRole.USER]: 'blue',
      [UserRole.GUEST]: 'green',
      [UserRole.AUDIT]: 'yellow',
    };
    return roleColorMap[role] || 'default';
  }

  getUserRoleName(role: UserRole): string {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        return Lang.t('user.roleAdministrator');
      case UserRole.USER:
        return Lang.t('user.roleUser');
      case UserRole.GUEST:
        return Lang.t('user.roleGuest');
      case UserRole.AUDIT:
        return Lang.t('user.roleAudit');
      default:
        return role;
    }
  }

  getSafeUsername(user: User): string {
    return user.username || Lang.t('user.unknown');
  }

  getSafeSpaceName(space: Space): string {
    return space.name || Lang.t('space.unknown');
  }

  renderUserOption = (user: User) => (
    <Option key={user.uuid} value={user.uuid}>
      <div className="user-option">
        <Avatar size="small" src={user.getAvatarUrl()} icon={<UserOutlined />} />
        <span className="username">{this.getSafeUsername(user)}</span>
        <Tag color={this.getUserRoleColor(user.role)} className="role-tag">
          {this.getUserRoleName(user.role)}
        </Tag>
      </div>
    </Option>
  );

  renderSpaceOption = (space: Space) => (
    <Option key={space.uuid} value={space.uuid}>
      <div className="space-option">
        <TeamOutlined />
        <span className="space-name">{this.getSafeSpaceName(space)}</span>
        <span className="space-info">
          ({space.getHumanizeTotalSize()} / {space.getHumanizeTotalSizeLimit()})
        </span>
      </div>
    </Option>
  );

  static open = (matterUuids: string) => {
    const currentUser = Moon.getSingleton().user;

    let modal = Modal.confirm({
      className: 'share-modal custom-handle-modal',
      title: Lang.t('share.shareDetail'),
      icon: <ExclamationCircleFilled twoToneColor="#FFDC00" />,
      width: '90vw',
      okCancel: false,
      maskClosable: true,
      content: (
        <ShareDialogModal
          matterUuids={matterUuids}
          onSuccess={() => {
            modal.destroy();
          }}
          onClose={() => {
            modal.destroy();
          }}
        />
      ),
    });
  };

  renderShareSettings() {
    const { expireValue, expireUnit, isInfinity, permission } = this.state;

    const unitOptions = ShareExpireOptionList.filter(
      option => option.value !== ShareExpireOption.INFINITY
    );

    return (
      <div className="share-settings-horizontal">
        <div className="settings-row">
          <div className="setting-item">
            <div className="setting-title">
              <FileTextOutlined className="mr5" />
              {Lang.t('share.expireTime')}
            </div>
            <div className="expire-control-group">
              <InputNumber
                min={1}
                value={expireValue}
                onChange={value => this.setState({ expireValue: value || 1 })}
                disabled={isInfinity}
                className="expire-number-input"
              />
              <Select
                value={expireUnit}
                onChange={(value: ShareExpireOption) =>
                  this.setState({ expireUnit: value })
                }
                disabled={isInfinity}
                className="expire-unit-select"
              >
                {unitOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {Lang.t(option.name)}
                  </Select.Option>
                ))}
              </Select>
              <Checkbox
                checked={isInfinity}
                onChange={e => this.setState({ isInfinity: e.target.checked })}
              >
                {Lang.t('share.infinity')}
              </Checkbox>
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-title">
              <EyeOutlined className="mr5" />
              {Lang.t('share.permission')}
            </div>
            <Radio.Group
              value={permission}
              onChange={e => this.setState({ permission: e.target.value })}
              className="permission-radio-group setting-control"
            >
              <Radio value="PREVIEW_ONLY">
                <span className="permission-label">{Lang.t('share.previewOnly')}</span>
              </Radio>
              <Radio value="FULL">
                <span className="permission-label">{Lang.t('share.fullPermission')}</span>
              </Radio>
            </Radio.Group>
          </div>
        </div>
      </div>
    );
  }

  renderPublicShare() {
    const { publicShare } = this.state;

    return (
      <div className="share-block">
        {publicShare ? (
          <>
            <div className="share-created-success">
              <div className="success-title">
                <span className="success-icon">✓</span>
                {Lang.t('share.createSuccess')}
              </div>
              <p className="success-tip">{Lang.t('share.copyTip')}</p>
            </div>

            <div>
              <img className="share-icon" src={publicShare.getIcon()} />
              <span className="name">{publicShare.name}</span>
            </div>
            <div className="mt15">
              <span className="inline-block mr10">
                {Lang.t('share.sharer')}：{publicShare.username || Lang.t('user.unknown')}
              </span>
              <span className="inline-block mr10">
                {publicShare.expireInfinity
                  ? Lang.t('share.noExpire')
                  : `${Lang.t('share.expireTime')}：${DateUtil.simpleDateHourMinute(
                      publicShare.expireTime
                    )}`}
              </span>
            </div>
            <div className="mt15 link-row">
              <span className="link-label">{Lang.t('share.link')}：</span>
              <span className="link-value">{publicShare.getLink()}</span>
              <CopyOutlined
                className="text-primary copy-icon"
                onClick={() => this.clipboard(publicShare.getLink())}
              />
            </div>
            {publicShare.code && (
              <div className="mt15 code-row">
                <span className="code-label">{Lang.t('share.code')}：</span>
                <span className="code-value">{publicShare.code}</span>
                <CopyOutlined
                  className="text-primary copy-icon"
                  onClick={() => this.clipboard(publicShare.code!)}
                />
              </div>
            )}
          </>
        ) : (
          <div className="public-share-prompt">
            <p className="prompt-text">{Lang.t('share.publicSharePrompt')}</p>
            {this.renderShareSettings()}
          </div>
        )}
      </div>
    );
  }

  renderPrivateShare() {
    const { selectedUsers, selectedSpaces, userPager, spacePager } = this.state;

    return (
      <div className="private-share-panel">
        <div className="selection-section">
          <div className="section-title">
            <UserOutlined />
            {Lang.t('share.selectUsers')}
          </div>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={Lang.t('share.selectUsersPlaceholder')}
            value={selectedUsers.map(user => user.uuid)}
            onChange={this.handleUserSelect}
            loading={userPager.loading}
            filterOption={(input, option) => {
              const user = userPager.data.find(u => u.uuid === option?.key);
              return user
                ? (user.username || '').toLowerCase().includes(input.toLowerCase())
                : false;
            }}
          >
            {userPager.data.map(user => this.renderUserOption(user))}
          </Select>
        </div>

        <div className="selection-section mt20">
          <div className="section-title">
            <TeamOutlined />
            {Lang.t('share.selectSpaces')}
          </div>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder={Lang.t('share.selectSpacesPlaceholder')}
            value={selectedSpaces.map(space => space.uuid)}
            onChange={this.handleSpaceSelect}
            loading={spacePager.loading}
            filterOption={(input, option) => {
              const space = spacePager.data.find(s => s.uuid === option?.key);
              return space
                ? (space.name || '').toLowerCase().includes(input.toLowerCase())
                : false;
            }}
          >
            {spacePager.data.map(space => this.renderSpaceOption(space))}
          </Select>
        </div>

        <div className="share-settings-private mt20">{this.renderShareSettings()}</div>

        {(selectedUsers.length > 0 || selectedSpaces.length > 0) && (
          <div className="selected-preview mt20">
            <div className="preview-title">{Lang.t('share.selectedTargets')}</div>
            {selectedUsers.length > 0 && (
              <div className="preview-section">
                <div className="preview-label">{Lang.t('user.username')}:</div>
                <div className="preview-tags">
                  {selectedUsers.map(user => (
                    <Tag
                      key={user.uuid}
                      color="blue"
                      closable
                      onClose={() => {
                        this.setState({
                          selectedUsers: selectedUsers.filter(u => u.uuid !== user.uuid),
                        });
                      }}
                    >
                      <Avatar size="small" src={user.getAvatarUrl()} />
                      {this.getSafeUsername(user)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {selectedSpaces.length > 0 && (
              <div className="preview-section">
                <div className="preview-label">{Lang.t('space.name')}:</div>
                <div className="preview-tags">
                  {selectedSpaces.map(space => (
                    <Tag
                      key={space.uuid}
                      color="green"
                      closable
                      onClose={() => {
                        this.setState({
                          selectedSpaces: selectedSpaces.filter(s => s.uuid !== space.uuid),
                        });
                      }}
                    >
                      <TeamOutlined />
                      {this.getSafeSpaceName(space)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { activeTab, selectedUsers, selectedSpaces, publicShare } = this.state;
    const disablePublicShare = this.user.disablePublicShare;

    return (
      <div className="widget-share-dialog-panel">
        <Tabs activeKey={activeTab} onChange={this.handleTabChange}>
          {!disablePublicShare && (
            <TabPane tab={Lang.t('share.publicLink')} key="public">
              {this.renderPublicShare()}
            </TabPane>
          )}
          <TabPane tab={Lang.t('share.privateShare')} key="private">
            {this.renderPrivateShare()}
          </TabPane>
        </Tabs>

        <div className="mt10 text-right">
          <Button className="mr10" onClick={() => this.props.onClose()}>
            {Lang.t('close')}
          </Button>

          {activeTab === 'public' ? (
            publicShare ? (
              <Button type="primary" onClick={this.copyPublicShare} icon={<CopyOutlined />}>
                {Lang.t('matter.copyLinkAndCode')}
              </Button>
            ) : (
              <Button type="primary" onClick={this.createPublicShare}>
                {Lang.t('share.createPublicShare')}
              </Button>
            )
          ) : (
            <Button
              type="primary"
              onClick={this.createPrivateShare}
              disabled={selectedUsers.length === 0 && selectedSpaces.length === 0}
            >
              {Lang.t('share.createPrivateShare')}
            </Button>
          )}
        </div>
      </div>
    );
  }
}